import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const KOH_PHANGAN = { lat: 9.7372, lng: 100.0266 };

const MapboxPicker = ({ isOpen, onClose, onLocationSelect, currentLocation, title }) => {
  const [position, setPosition] = useState(
    currentLocation
      ? { lat: currentLocation?.lat || currentLocation?.[0] || KOH_PHANGAN.lat, lng: currentLocation?.lng || currentLocation?.[1] || KOH_PHANGAN.lng }
      : KOH_PHANGAN
  );
  const [address, setAddress] = useState('');
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(null);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const positionRef = useRef(position);

  useEffect(() => { positionRef.current = position; }, [position]);

  // Load Leaflet CSS + JS
  useEffect(() => {
    if (!isOpen) return;

    const loadLeaflet = () => {
      if (window.L) { setMapReady(true); return; }

      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }

      if (!document.getElementById('leaflet-js')) {
        const script = document.createElement('script');
        script.id = 'leaflet-js';
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.async = true;
        script.onload = () => setMapReady(true);
        script.onerror = () => setMapError('Failed to load map library');
        document.head.appendChild(script);
      } else {
        // Script tag exists but may still be loading
        const existing = document.getElementById('leaflet-js');
        existing.addEventListener('load', () => setMapReady(true));
      }
    };

    loadLeaflet();
  }, [isOpen]);

  // Initialize map
  useEffect(() => {
    if (!isOpen || !mapReady || !mapContainerRef.current || mapRef.current) return;

    try {
      const L = window.L;

      const map = L.map(mapContainerRef.current, {
        center: [positionRef.current.lat, positionRef.current.lng],
        zoom: 15,
        zoomControl: true,
      });

      // CartoDB Positron — detailed, shows buildings, roads, places, free & no key
      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 20,
      }).addTo(map);

      // Custom marker icon
      const markerIcon = L.divIcon({
        className: '',
        html: `<div style="
          width:36px;height:36px;
          background:#6366f1;
          border:3px solid white;
          border-radius:50% 50% 50% 0;
          transform:rotate(-45deg);
          box-shadow:0 2px 8px rgba(0,0,0,0.35);
        "></div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 36],
      });

      const marker = L.marker([positionRef.current.lat, positionRef.current.lng], {
        icon: markerIcon,
        draggable: true,
      }).addTo(map);

      marker.on('dragend', (e) => {
        const { lat, lng } = e.target.getLatLng();
        setPosition({ lat, lng });
        fetchAddress(lat, lng);
      });

      map.on('click', (e) => {
        const { lat, lng } = e.latlng;
        setPosition({ lat, lng });
        marker.setLatLng([lat, lng]);
        fetchAddress(lat, lng);
      });

      mapRef.current = map;
      markerRef.current = marker;

      // Fetch address for initial position
      fetchAddress(positionRef.current.lat, positionRef.current.lng);

      // Fix tile rendering after modal opens
      setTimeout(() => map.invalidateSize(), 200);
    } catch (err) {
      setMapError('Error initializing map: ' + err?.message);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, [isOpen, mapReady]);

  // Sync marker when position state changes (e.g. from search)
  useEffect(() => {
    if (markerRef.current && mapRef.current) {
      markerRef.current.setLatLng([position.lat, position.lng]);
      mapRef.current.flyTo([position.lat, position.lng], 16, { duration: 0.8 });
    }
  }, [position]);

  const fetchAddress = async (lat, lng) => {
    setIsLoadingAddress(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=en`,
        { headers: { 'Accept-Language': 'en' } }
      );
      let data = await res.json();
      if (data?.display_name) {
        setAddress(data.display_name);
      } else {
        setAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
      }
    } catch {
      setAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const performSearch = async (query) => {
    setIsSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(query)}&limit=8&accept-language=en&viewbox=99.8,10.0,100.2,9.5&bounded=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      let data = await res.json();

      // If no bounded results, search globally
      if (!data?.length) {
        const res2 = await fetch(
          `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(query + ' Koh Phangan Thailand')}&limit=6&accept-language=en`,
          { headers: { 'Accept-Language': 'en' } }
        );
        data = await res2.json();
      }

      setSearchResults(data || []);
      setShowSuggestions((data || []).length > 0);
    } catch {
      setSearchResults([]);
      setShowSuggestions(false);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (searchQuery?.trim()?.length >= 2) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => performSearch(searchQuery), 500);
    } else {
      setSearchResults([]);
      setShowSuggestions(false);
    }
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchQuery]);

  const handleSelectSuggestion = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setPosition({ lat, lng });
    setAddress(result.display_name);
    setSearchQuery(result.display_name);
    setShowSuggestions(false);
    setSearchResults([]);
  };

  const handleConfirm = () => {
    onLocationSelect({
      coordinates: [position.lat, position.lng],
      address: address || `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[998]" onClick={onClose} />
      <div className="fixed inset-1 md:inset-2 lg:inset-3 bg-background rounded-lg shadow-xl z-[999] flex flex-col overflow-hidden max-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <div>
            <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Icon name="Map" size={24} className="text-indigo-600" />
              {title || 'Select Location'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {mapError ? 'Map loading failed' : 'Tap on the map or drag the pin to set location'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-secondary transition-colors flex items-center justify-center"
            aria-label="Close map"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Search bar */}
        <div className="p-4 border-b border-border flex-shrink-0 relative z-10">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder="Search place on Koh Phangan..."
              className="w-full px-4 py-3 pl-12 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-600"
            />
            <Icon
              name={isSearching ? 'Loader2' : 'Search'}
              size={20}
              className={`absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground ${isSearching ? 'animate-spin' : ''}`}
            />
            {searchQuery?.length > 0 && (
              <button
                onClick={() => { setSearchQuery(''); setShowSuggestions(false); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            )}

            {showSuggestions && searchResults?.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-background border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto z-20">
                {searchResults.map((result, index) => (
                  <button
                    key={index}
                    onClick={() => handleSelectSuggestion(result)}
                    className="w-full px-4 py-3 text-left hover:bg-secondary transition-colors border-b border-border last:border-b-0 flex items-start gap-3"
                  >
                    <Icon name="MapPin" size={18} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {result?.name || result?.display_name?.split(',')[0]}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{result?.display_name}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Map container */}
        <div className="flex-1 relative min-h-0">
          {mapError ? (
            <div className="absolute inset-0 flex items-center justify-center bg-secondary">
              <div className="text-center p-6">
                <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-3" />
                <p className="text-foreground font-medium">Map failed to load</p>
                <p className="text-sm text-muted-foreground mt-1">{mapError}</p>
              </div>
            </div>
          ) : !mapReady ? (
            <div className="absolute inset-0 flex items-center justify-center bg-secondary">
              <div className="text-center">
                <Icon name="Loader2" size={40} className="text-indigo-600 mx-auto mb-3 animate-spin" />
                <p className="text-muted-foreground text-sm">Loading map…</p>
              </div>
            </div>
          ) : null}
          <div ref={mapContainerRef} className="w-full h-full" style={{ minHeight: '300px' }} />
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex-shrink-0 bg-background">
          <div className="flex items-start gap-3 mb-3">
            <Icon name="MapPin" size={18} className="text-indigo-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground mb-0.5">Selected location</p>
              {isLoadingAddress ? (
                <div className="flex items-center gap-2">
                  <Icon name="Loader2" size={14} className="animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Getting address…</span>
                </div>
              ) : (
                <p className="text-sm font-medium text-foreground line-clamp-2">
                  {address || `${position?.lat?.toFixed(6)}, ${position?.lng?.toFixed(6)}`}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 border border-border rounded-lg text-foreground hover:bg-secondary transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoadingAddress}
              className="flex-1 py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Icon name="Check" size={18} />
              Confirm Location
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MapboxPicker;